import { FiSearch } from "react-icons/fi";
import { IoCall, IoPersonCircleOutline, IoTrashOutline } from "react-icons/io5";
import { FaVideo } from "react-icons/fa6";
import { HiOutlinePhotograph } from "react-icons/hi";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import {
  toggleProfileView,
  updateViewState,
} from "../../../store/Slices/ViewManagerSlice";
import TextTranslate from "../../../utils/TextTranslate";
import { useNavigate } from "react-router-dom";
import useApiPost from "../../../hooks/PostData";
import { CallUserRes } from "../../../types/ResType";
import { updatePeerData } from "../../../store/Slices/PeerJsSlice";
import { MeetingScheduleModal } from "../../../components/MeetingSchedule";
import { useDeviceCheck } from "../../../hooks/useCameraCheck";
import toast from "react-hot-toast";
import axios from "axios";
import { useRef } from "react";
import { setCallPeerSnapshot } from "../../../utils/callPeerSnapshot";

const menuBtn =
  "group flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg py-2 pl-4 pr-3 text-left data-[focus]:bg-dropdownOptionHover";

const menuLabel =
  "min-w-0 flex-1 truncate text-left leading-5";

export default function RightSideOptions() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const { postData } = useApiPost();
  const { checkDevices } = useDeviceCheck();
  const peerSession = useAppSelector((s) => s.PeerJsSlice);
  const callInFlight = useRef(false);

  async function callUser(call_type: "video" | "audio") {
    if (callInFlight.current) return;
    if (peerSession.call_joined && Number(peerSession.call_id) > 0) {
      toast.error("Already in a call");
      return;
    }
    callInFlight.current = true;
    const result = await checkDevices(call_type);

    // Audio: only mic is required. Video: mic + camera.
    if (result.microphoneError) {
      callInFlight.current = false;
      navigate("/video-call");
      return;
    }
    if (call_type === "video" && result.cameraError) {
      callInFlight.current = false;
      navigate("/video-call");
      return;
    }

    try {
      const resData: CallUserRes = await postData("call/make-call", {
        chat_id: currentConversationData.chat_id,
        call_type,
      });

      sessionStorage.setItem("call_type", call_type);
      sessionStorage.setItem(
        "call_chat_type",
        currentConversationData.chat_type,
      );
      sessionStorage.setItem("is_caller", "true");
      sessionStorage.setItem("call_joined", "true");
      sessionStorage.setItem("room_id", resData.call.room_id);
      sessionStorage.setItem("call_id", resData.call.call_id.toString());
      sessionStorage.setItem(
        "chat_id",
        currentConversationData.chat_id.toString(),
      );
      const isGroupCall =
        currentConversationData.chat_type === "group" ||
        currentConversationData.chat_type === "public_group";
      const callTitle = isGroupCall
        ? currentConversationData.group_name || "Group call"
        : currentConversationData.full_name ||
          currentConversationData.user_name ||
          "Call";
      const callAvatar = isGroupCall
        ? currentConversationData.group_icon || ""
        : currentConversationData.profile_pic || "";
      setCallPeerSnapshot({
        title: callTitle,
        avatar: callAvatar,
        chatId: currentConversationData.chat_id,
        chatType: currentConversationData.chat_type,
      });
      const engine =
        resData.call.media_engine || "livekit";
      sessionStorage.setItem("media_engine", engine);
      if (resData.call.livekit_token) {
        sessionStorage.setItem("livekit_token", resData.call.livekit_token);
      } else {
        sessionStorage.removeItem("livekit_token");
      }
      if (resData.call.livekit_url) {
        sessionStorage.setItem("livekit_url", resData.call.livekit_url);
      } else {
        sessionStorage.removeItem("livekit_url");
      }

      dispatch(
        updatePeerData({
          room_id: resData.call.room_id,
          call_id: resData.call.call_id,
          chat_id: currentConversationData.chat_id.toString(),
          isVideoEnabled: call_type === "video",
          isAudioEnabled: true,
          call_joined: true,
        }),
      );

      navigate("/video-call");
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : 0;
      const code = axios.isAxiosError(error)
        ? String((error.response?.data as { error?: string })?.error || "")
        : "";
      if (status === 409 || code === "peer_busy" || code === "already_in_call") {
        toast.error(
          code === "already_in_call"
            ? "Already in a call"
            : "User is busy",
        );
      } else {
        console.error("Error initiating call:", error);
      }
    } finally {
      callInFlight.current = false;
    }
  }

  return (
    <>
      <MeetingScheduleModal />
      <div className="flex items-center gap-1.5 sm:gap-2.5 lg:gap-3">
        <button
          type="button"
          title="Audio Call"
          aria-label="Audio Call"
          onClick={() => void callUser("audio")}
          className="grid h-9 w-9 place-content-center rounded-full text-darkText transition active:scale-95 hover:bg-black/[0.06] dark:hover:bg-white/10"
        >
          <IoCall className="text-xl sm:text-[1.35rem]" />
        </button>

        <button
          type="button"
          title="Video Call"
          aria-label="Video Call"
          onClick={() => void callUser("video")}
          className="grid h-9 w-9 place-content-center rounded-full text-darkText transition active:scale-95 hover:bg-black/[0.06] dark:hover:bg-white/10"
        >
          <FaVideo className="text-lg sm:text-xl" />
        </button>

        <Menu>
          <MenuButton className="grid h-9 w-9 place-content-center rounded-full text-darkText transition focus:outline-none data-[focus]:outline-1 hover:bg-black/[0.06] dark:hover:bg-white/10">
            <PiDotsThreeVerticalBold className="text-2xl" />
          </MenuButton>

          <MenuItems
            transition
            anchor="bottom end"
            className="z-[60] w-60 origin-top-right rounded-xl border border-borderColor bg-modalBg p-1 text-sm/6 transition duration-200 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
          >
            <MenuItem>
              <button
                type="button"
                onClick={() =>
                  dispatch(updateViewState({ showSearchMessage: true }))
                }
                className={menuBtn}
              >
                <span className={menuLabel}>
                  <TextTranslate text="Search Messages" />
                </span>
                <FiSearch className="shrink-0 text-lg text-lightText" />
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                onClick={() => dispatch(toggleProfileView(true))}
                className={menuBtn}
              >
                <span className={menuLabel}>
                  <TextTranslate
                    text={
                      currentConversationData.chat_type == "group"
                        ? "View Group"
                        : "View Profile"
                    }
                  />
                </span>
                <IoPersonCircleOutline className="shrink-0 text-xl text-lightText" />
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                onClick={() =>
                  dispatch(
                    updateViewState({
                      showMediaDocLinks: true,
                      showOtherProfile: true,
                    }),
                  )
                }
                className={menuBtn}
              >
                <span className={menuLabel}>
                  <TextTranslate text="Media, Links, and docs" />
                </span>
                <HiOutlinePhotograph className="shrink-0 text-lg text-lightText" />
              </button>
            </MenuItem>
            <MenuItem>
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    updateMessageOptions({
                      showModal: true,
                      title: "Are you sure you want to clear this chat?",
                      description:
                        "All messages in this chat will be removed from your device. This action cannot be undone.",
                      modalName: "clear_chat",
                    }),
                  );
                }}
                className={`${menuBtn} text-[#FF3B30]`}
              >
                <span className={menuLabel}>
                  <TextTranslate text="Clear Chat" />
                </span>
                <IoTrashOutline className="shrink-0 text-lg" />
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
    </>
  );
}
